/**
 * Poll-votes data layer: the caller's own vote plus the upsert write.
 *
 * `useMyPollVote()` reads `poll-votes.mine` (the caller's vote, or null) — the
 * per-option tally is read separately from `useCommentStats().breakdowns.poll`,
 * not here. `setPollVoteAsync()` upserts via `poll-votes.set` (an empty option
 * clears the vote), optimistically updating both the `mine` cache and the poll
 * breakdown on the `comments.stats` cache so the survey→results switch and the
 * percentages don't wait on a refetch; it rolls back both on error and
 * invalidates both on settle. Writes are serialized per space so a rapid
 * vote/change/clear sequence converges instead of racing.
 */
import type { CommentStatsResult } from '@repo/shared/orpc/procedures/comments';
import type { PollVote } from '@repo/shared/orpc/procedures/poll-votes';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore, usePersonaKey } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { siloParamsOf } from './silo-params';
import { useInstanceQuery } from './use-silo-query';

export type { PollVote } from '@repo/shared/orpc/procedures/poll-votes';

/**
 * @public
 */
export function useMyPollVote(): PollVote | null {
    const personaKey = usePersonaKey();
    const utils = useApiQueryUtils();
    // `mine` is authenticated; don't fire a guaranteed 401 for logged-out
    // viewers (they still see the tally via comments.stats).
    const { data } = useInstanceQuery({ procedure: utils.pollVotes.mine, enabled: !!personaKey });
    return data ?? null;
}

// Apply a vote change to a cached stats breakdown: decrement the caller's
// previous option, increment the next. Keeps the percentages from flashing
// stale between the optimistic write and the refetch.
function patchPollBreakdown(
    stats: CommentStatsResult | undefined,
    previousOption: string | null,
    nextOption: string | null,
): CommentStatsResult | undefined {
    if (!stats) return stats;
    const poll = { ...(stats.breakdowns.poll ?? {}) };
    if (previousOption) {
        const remaining = (poll[previousOption] ?? 0) - 1;
        if (remaining > 0) poll[previousOption] = remaining;
        else delete poll[previousOption];
    }
    if (nextOption) poll[nextOption] = (poll[nextOption] ?? 0) + 1;
    return { ...stats, breakdowns: { ...stats.breakdowns, poll } };
}

// The tail of each space's write chain: the promise for the most recently
// queued write, keyed by spaceKey. Absent once a space's chain has drained.
const pendingWritesBySpace = new Map<string, Promise<void>>();

/**
 * Run `run` only after the previous write for the same space has finished, so
 * writes to one poll happen one at a time in call order.
 *
 * Why serialize: each write applies an optimistic cache patch and then, on
 * settle, invalidates + refetches (see `runSetPollVoteAsync`). If two writes
 * overlap, the earlier one's refetch can land *after* the later one's
 * optimistic patch and briefly clobber it with stale server data — a visible
 * flicker (e.g. changing Spring→Summer momentarily snapping back to Spring).
 * Serializing keeps the sequence monotonic so it converges on the last write
 * without flicker. Correctness alone wouldn't require this — every write
 * invalidates on settle, so the caches always reconcile with the server
 * eventually; serialization is about avoiding the transient wrong state.
 *
 * Carried over from the `upvotes`/`reactions` data layer, where it earns its
 * keep on rapid on/off toggling; a poll vote is a slower multi-step
 * interaction, so here it's cheap insurance rather than a hot path.
 *
 * How the chaining works:
 * - `previous` is the current tail for this space (or an already-resolved
 *   promise if the chain is empty), and `next = previous.then(run, run)` queues
 *   `run` to start when `previous` settles. Passing `run` as *both* the
 *   fulfill and reject handler means a failed write doesn't stall the queue —
 *   the next write still runs.
 * - `next` becomes the new tail. When it settles, `clearIfCurrent` removes the
 *   map entry, but only if `next` is still the tail: if another write has
 *   already chained on, it replaced the tail and must not be deleted, otherwise
 *   the map would leak (or drop) a live chain.
 */
function withSerializedPollVoteWrites(spaceKey: string, run: () => Promise<void>): Promise<void> {
    const previous = pendingWritesBySpace.get(spaceKey) ?? Promise.resolve();
    const next = previous.then(run, run);
    pendingWritesBySpace.set(spaceKey, next);
    const clearIfCurrent = () => {
        if (pendingWritesBySpace.get(spaceKey) === next) pendingWritesBySpace.delete(spaceKey);
    };
    next.then(clearIfCurrent, clearIfCurrent);
    return next;
}

/**
 * @public
 */
export function setPollVoteAsync({ datastore, pollOption }: { datastore: Datastore; pollOption: string }): Promise<void> {
    const spaceKey = datastore.getInstanceKey();
    return withSerializedPollVoteWrites(spaceKey, () => runSetPollVoteAsync({ datastore, pollOption }));
}

async function runSetPollVoteAsync({ datastore, pollOption }: { datastore: Datastore; pollOption: string }): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const mineKey = utils.pollVotes.mine.queryOptions({ input: { siloKey, locale, spaceKey } }).queryKey;
    const statsKey = utils.comments.stats.queryOptions({ input: { siloKey, locale, spaceKey } }).queryKey;

    // onMutate: cancel in-flight reads, snapshot both caches, apply locally.
    await Promise.all([queryClient.cancelQueries({ queryKey: mineKey }), queryClient.cancelQueries({ queryKey: statsKey })]);
    const previousMine = queryClient.getQueryData<PollVote | null>(mineKey);
    const previousStats = queryClient.getQueryData<CommentStatsResult>(statsKey);

    const nextVote: PollVote | null = pollOption
        ? {
              pollVoteKey: previousMine?.pollVoteKey ?? '',
              spaceKey,
              authorKey: datastore.getPersonaKey() ?? '',
              pollOption,
              createdAt: previousMine?.createdAt ?? new Date().toISOString(),
          }
        : null;
    queryClient.setQueryData(mineKey, nextVote);
    queryClient.setQueryData(statsKey, patchPollBreakdown(previousStats, previousMine?.pollOption ?? null, nextVote?.pollOption ?? null));

    try {
        await datastore.getApiClient().pollVotes.set({ siloKey, locale, spaceKey, pollOption });
    } catch (err) {
        // onError: restore both caches to their pre-mutation snapshots.
        queryClient.setQueryData(mineKey, previousMine);
        queryClient.setQueryData(statsKey, previousStats);
        throw err;
    } finally {
        // onSettled: invalidate so both caches reconcile with the server.
        fireAndForget(queryClient.invalidateQueries({ queryKey: mineKey }));
        fireAndForget(queryClient.invalidateQueries({ queryKey: statsKey }));
    }
}
